/**
 * Cleans HTML pasted into the note editor.
 *
 * Fathom's "copy summary" wraps every line in a link back to the recording
 * timestamp, so a pasted summary arrives as a wall of underlined text. Word
 * and Google Docs bring inline styles and classes the editor ignores but
 * that bloat the stored body. This strips both while keeping links a person
 * actually put inside a sentence.
 *
 * Pure: DOM in, HTML out. Runs in the browser (TipTap's transformPastedHTML)
 * and under jsdom in tests.
 */

/** Hosts whose links are navigation chrome, never content. */
const CHROME_LINK_HOSTS: readonly string[] = ['fathom.video', 'www.fathom.video'];

/** Attributes that carry no meaning for a note and are dropped everywhere. */
const NOISE_ATTRIBUTES: readonly string[] = ['style', 'class', 'id', 'dir', 'lang'];

/** Block elements whose whole text being one link marks the link as chrome. */
const BLOCK_TAGS: ReadonlySet<string> = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TD', 'BLOCKQUOTE']);

function hostOf(href: string): string | null {
  try {
    return new URL(href, 'https://placeholder.invalid').hostname.toLowerCase();
  } catch {
    return null;
  }
}

function text(node: Node): string {
  return (node.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Whether an anchor is chrome (unwrap) rather than a link someone meant.
 *
 * @param anchor - The anchor element.
 * @returns True when the anchor should be replaced by its text.
 */
export function isChromeLink(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute('href') ?? '';
  const host = hostOf(href);
  if (!href.trim() || href.trim() === '#' || href.trim().toLowerCase().startsWith('javascript:')) return true;
  if (host && CHROME_LINK_HOSTS.includes(host)) return true;
  const label = text(anchor);
  if (label === '' || label === href.trim()) return true;
  // A link that IS the whole line (Fathom's per-line pattern) is chrome; a
  // link inside a sentence is content.
  const block = anchor.parentElement;
  if (block && BLOCK_TAGS.has(block.tagName) && text(block) === label) return true;
  return false;
}

/**
 * Cleans pasted HTML: unwraps chrome links, strips presentational attributes,
 * and drops empty anchors. Everything else passes through for the editor's
 * own schema to filter.
 *
 * @param html - The clipboard HTML.
 * @returns Cleaned HTML.
 */
export function cleanPastedHtml(html: string): string {
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  for (const anchor of Array.from(body.querySelectorAll('a'))) {
    if (isChromeLink(anchor)) {
      const parent = anchor.parentNode;
      if (!parent) continue;
      while (anchor.firstChild) parent.insertBefore(anchor.firstChild, anchor);
      parent.removeChild(anchor);
    }
  }

  for (const element of Array.from(body.querySelectorAll('*'))) {
    for (const name of NOISE_ATTRIBUTES) element.removeAttribute(name);
    for (const attr of Array.from(element.attributes)) {
      if (attr.name.startsWith('data-') || attr.name.startsWith('on')) element.removeAttribute(attr.name);
    }
  }

  return body.innerHTML;
}
